import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ROUTES } from "@/app/routes";
import { usePostApiConnectorInstalls } from "@/lib/api/generated/channels/channels";
import { postApiProvidersDescriptors } from "@/lib/api/generated/providers/providers";
import {
  createProviderInstallFieldValues,
  getInstallableProviderDescriptors,
  parseProviderInstallFieldValues,
} from "./provider-descriptor-helpers";

const createConnectorSchema = z.object({
  id: z.string().min(1, "ID is required"),
  descriptorKey: z.string().min(1, "Backend is required"),
  status: z.enum(["active", "paused", "error"]).default("active"),
  fieldValues: z.record(z.string()).default({}),
});

type CreateConnectorForm = z.infer<typeof createConnectorSchema>;

export function CreateConnectorPage() {
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const mutation = usePostApiConnectorInstalls();
  const { data: providerDescriptors = [], isLoading: providersLoading } = useQuery({
    queryKey: ["providers-descriptors", tenantId],
    queryFn: () => postApiProvidersDescriptors({ tenantId }),
    enabled: Boolean(tenantId),
  });
  const installableProviders = useMemo(
    () => getInstallableProviderDescriptors(providerDescriptors),
    [providerDescriptors],
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<CreateConnectorForm>({
    resolver: zodResolver(createConnectorSchema),
    defaultValues: {
      id: `ci_connector_${Date.now()}`,
      descriptorKey: "",
      status: "active",
      fieldValues: {},
    },
  });

  const descriptorKey = watch("descriptorKey");
  const selectedDescriptor =
    installableProviders.find((record) => record.descriptorKey === descriptorKey) ??
    installableProviders[0];

  useEffect(() => {
    if (descriptorKey || installableProviders.length === 0) {
      return;
    }

    const fallback = installableProviders[0];
    if (!fallback) {
      return;
    }

    setValue("descriptorKey", fallback.descriptorKey);
    setValue(
      "id",
      `ci_${toInstallIdSegment(fallback.connectorKey ?? fallback.providerKey)}_${Date.now()}`,
    );
    setValue("fieldValues", createProviderInstallFieldValues(fallback));
  }, [descriptorKey, installableProviders, setValue]);

  const handleDescriptorChange = (nextDescriptorKey: string) => {
    const nextDescriptor = installableProviders.find((record) => record.descriptorKey === nextDescriptorKey);
    setValue("descriptorKey", nextDescriptorKey);
    clearErrors("descriptorKey");
    setValue(
      "id",
      `ci_${toInstallIdSegment(nextDescriptor?.connectorKey ?? nextDescriptor?.providerKey ?? "connector")}_${Date.now()}`,
    );
    setValue("fieldValues", createProviderInstallFieldValues(nextDescriptor));
  };

  const onSubmit = async (data: CreateConnectorForm) => {
    const descriptor = installableProviders.find((record) => record.descriptorKey === data.descriptorKey);
    if (!descriptor?.connectorKey) {
      setError("descriptorKey", {
        type: "manual",
        message: "Choose an installable backend.",
      });
      return;
    }

    const parsed = parseProviderInstallFieldValues(descriptor, data.fieldValues, {
      tenantId: tenantId!,
    });
    if (!parsed.ok) {
      setError(`fieldValues.${parsed.fieldKey}` as const, {
        type: "manual",
        message: parsed.message,
      });
      return;
    }
    clearErrors("fieldValues");

    try {
      await mutation.mutateAsync({
        data: {
          id: data.id,
          tenantId: tenantId!,
          connectorKey: descriptor.connectorKey,
          status: data.status,
          credentialsRef: parsed.credentialsRef,
          config: parsed.config,
        },
      });
      toast.success(`Backend "${descriptor.displayName}" installed`);
      navigate(ROUTES.tenantConnectorInstalls(tenantId));
    } catch {
      toast.error("Failed to install backend");
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <h1 className="text-lg font-semibold mb-6" style={{ color: "var(--text-strong)" }}>
        Install Backend
      </h1>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <FormField label="Install ID" error={errors.id?.message}>
              <input {...register("id")} className="form-input font-mono" />
            </FormField>

            <FormField label="Backend" error={errors.descriptorKey?.message}>
              <select
                value={descriptorKey || selectedDescriptor?.descriptorKey || ""}
                onChange={(event) => handleDescriptorChange(event.target.value)}
                className="form-input"
                disabled={providersLoading || installableProviders.length === 0}
              >
                <option value="">Select backend</option>
                {installableProviders.map((record) => (
                  <option key={record.descriptorKey} value={record.descriptorKey}>
                    {record.displayName} ({record.connectorKey ?? record.providerKey})
                  </option>
                ))}
              </select>
              {selectedDescriptor?.description ? (
                <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                  {selectedDescriptor.description}
                </p>
              ) : null}
            </FormField>

            <FormField label="Status">
              <select {...register("status")} className="form-input">
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="error">Error</option>
              </select>
            </FormField>

            {(selectedDescriptor?.install?.fields ?? []).map((field) => (
              <FormField
                key={field.key}
                label={field.label}
                error={errors.fieldValues?.[field.key]?.message}
              >
                {field.type === "json" ? (
                  <textarea
                    {...register(`fieldValues.${field.key}` as const)}
                    rows={4}
                    className="form-input font-mono text-xs"
                    placeholder={field.placeholder ?? "{}"}
                  />
                ) : (
                  <input
                    {...register(`fieldValues.${field.key}` as const)}
                    className="form-input font-mono"
                    placeholder={field.placeholder}
                  />
                )}
                {field.description ? (
                  <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                    {field.description}
                  </p>
                ) : null}
              </FormField>
            ))}

            {selectedDescriptor ? (
              <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                This form is rendered from the provider descriptor for{" "}
                <span className="font-mono">{selectedDescriptor.providerKey}</span>.
              </p>
            ) : null}

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={isSubmitting || !selectedDescriptor}>
                {isSubmitting ? "Installing..." : "Install Backend"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate(ROUTES.tenantConnectorInstalls(tenantId))}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function toInstallIdSegment(value: string) {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return normalized || "connector";
}

function FormField({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </label>
      {children}
      {error ? (
        <span className="text-xs" style={{ color: "var(--danger)" }}>
          {error}
        </span>
      ) : null}
    </div>
  );
}
